import { useState, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Snackbar,
  Alert,
  InputAdornment,
  useTheme,
  Paper,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Card,
  Divider
} from "@mui/material";
import { API_ENDPOINTS } from "../../config.js";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import PersonIcon from "@mui/icons-material/Person";
import MessageIcon from "@mui/icons-material/Message";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import WorkIcon from "@mui/icons-material/Work";
import { CheckCircle, WhatsApp, Call, Email } from "@mui/icons-material";

// Debounce utility function
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Maps HTTP status codes / API error messages to user-friendly text
const getFriendlyError = (status, data) => {
  if (status === 429) {
    return 'Too many submissions attempted. Please try again later.';
  }
  if (status === 503 || status === 502) {
    return 'Our service is temporarily unavailable. Please try again shortly.';
  }
  if (status === 500) {
    return 'Something went wrong on our end. Please try again later.';
  }
  // Fallback to API message if it exists and is user-safe, otherwise generic
  return data?.error || data?.detail || 'Failed to send message. Please try again.';
};

const Contact = () => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "+91",
    message:
      "I am interested in learning more about your insurance plans. Please contact me.",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const validateForm = useCallback(() => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    } else if (!/^[a-zA-Z\s]+$/.test(formData.name.trim())) {
      newErrors.name = "Name can only contain letters and spaces";
    }

    // Email validation with proper regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Phone validation - Indian mobile numbers
    const phoneRegex = /^[6-9]\d{9}$/;
    const phoneDigits = formData.phone.replace("+91", "");
    if (!phoneDigits) {
      newErrors.phone = "Phone number is required";
    } else if (!phoneRegex.test(phoneDigits)) {
      newErrors.phone = "Enter valid 10-digit mobile number starting with 6-9";
    }

    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // Debounced validation for real-time feedback
  const debouncedValidate = useMemo(
    () => debounce((fieldName, value) => {
      const tempFormData = { ...formData, [fieldName]: value };
      const newErrors = {};

      switch (fieldName) {
        case 'name':
          if (value.trim() && value.trim().length < 2) {
            newErrors.name = "Name must be at least 2 characters";
          } else if (value.trim() && !/^[a-zA-Z\s]+$/.test(value.trim())) {
            newErrors.name = "Name can only contain letters and spaces";
          }
          break;
        case 'email':
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (value && !emailRegex.test(value)) {
            newErrors.email = "Please enter a valid email address";
          }
          break;
        case 'phone':
          const phoneRegex = /^[6-9]\d{9}$/;
          const phoneDigits = value.replace("+91", "");
          if (phoneDigits && !phoneRegex.test(phoneDigits)) {
            newErrors.phone = "Enter valid 10-digit mobile number starting with 6-9";
          }
          break;
        case 'message':
          if (value.trim() && value.trim().length < 10) {
            newErrors.message = "Message must be at least 10 characters";
          }
          break;
        default:
          break;
      }

      setErrors(prev => ({
        ...prev,
        [fieldName]: newErrors[fieldName] || ""
      }));
    }, 300),
    [formData]
  );

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({
      ...prev,
      open: false
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(API_ENDPOINTS.CONTACT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: formData.phone, // Already includes +91 prefix
          message: formData.message.trim()
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSnackbar({
          open: true,
          message: 'Message sent successfully!',
          severity: 'success'
        });
        
        // Reset form with default values
        setFormData({
          name: "",
          email: "",
          phone: "+91",
          message: "I am interested in learning more about your insurance plans. Please contact me.",
        });
      } else {
        throw new Error(getFriendlyError(response.status, data));
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Something went wrong. Please try again later.',
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;

    // Clear existing error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    let processedValue = value;

    if (name === "phone") {
      const digits = value.replace(/\D/g, "").slice(0, 10);
      processedValue = "+91" + digits;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: processedValue,
    }));

    // Trigger debounced validation
    debouncedValidate(name, processedValue);
  }, [errors, debouncedValidate]);

  return (
    <Box
      component="section"
      id="contact"
      sx={{
        position: "relative",
        overflow: "hidden",
        borderRadius: 4,
        boxShadow: theme.shadows[10],
        bgcolor: "background.paper",
        "&:before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "8px",
          background: (theme) => theme.palette.mode === 'dark'
            ? "linear-gradient(90deg, #90caf9 0%, #64b5f6 100%)"
            : "linear-gradient(90deg, #1976d2 0%, #0d47a1 100%)",
        },
      }}
    >
      <Box sx={{ p: { xs: 3, md: 6 } }}>
        <Typography
          variant="h4"
          component="h2"
          align="center"
          sx={{
            mb: 1,
            fontWeight: 700,
            background: (theme) => theme.palette.mode === 'dark'
              ? "linear-gradient(90deg, #90caf9 0%, #64b5f6 100%)"
              : "linear-gradient(90deg, #1976d2 0%, #0d47a1 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            display: "inline-block",
            width: "100%",
            textAlign: "center",
          }}
        >
          Request a Callback
        </Typography>

        <Typography
          variant="subtitle1"
          align="center"
          sx={{
            mb: 4,
            color: "text.secondary",
            maxWidth: "600px",
            mx: "auto",
          }}
        >
          Fill out the form below and our insurance expert will contact you
          shortly
        </Typography>

        {/* Quick Contact Options */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Or Contact Us Directly
          </Typography>
          <Grid container spacing={2} justifyContent="center">
            <Grid item>
              <Button
                variant="contained"
                color="success"
                startIcon={<PhoneIcon />}
                href="https://wa.me/916281648568?text=Hi%20PolicyTap,%20I'm%20interested%20in%20learning%20about%20insurance%20plans.%20Please%20help%20me."
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  bgcolor: '#25D366',
                  '&:hover': { bgcolor: '#128C7E' },
                  fontWeight: 600,
                  px: 3,
                  py: 1
                }}
              >
                WhatsApp Chat
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                startIcon={<PhoneIcon />}
                href="tel:+916281648568"
                sx={{ fontWeight: 600, px: 3, py: 1 }}
              >
                Call Now
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                startIcon={<EmailIcon />}
                href="mailto:contact@policytap.in?subject=Insurance Inquiry&body=Hi PolicyTap, I'm interested in learning about insurance plans. Please contact me."
                sx={{ fontWeight: 600, px: 3, py: 1 }}
              >
                Email Us
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Office Location Map */}
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
            📍 Visit Our Office
          </Typography>
          <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
            <Box sx={{ 
              width: '100%', 
              height: '300px', 
              borderRadius: 2, 
              overflow: 'hidden',
              position: 'relative'
            }}>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3806.0!2d78.4867!3d17.3850!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTfCsDIzJzA2LjAiTiA3OMKwMjknMTIuMSJF!5e0!3m2!1sen!2sin!4v1635000000000!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="PolicyTap Office Location - Secunderabad"
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              📍 Secunderabad, Telangana, India | 🕒 Mon-Sat: 9:00 AM - 6:00 PM
            </Typography>
          </Paper>
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={12} md={8} sx={{ mx: "auto" }}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="name"
                    placeholder="Your Name"
                    variant="outlined"
                    value={formData.name}
                    onChange={handleChange}
                    error={!!errors.name}
                    helperText={errors.name}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        backgroundColor: "background.paper",
                        "&:hover fieldset": {
                          borderColor: "primary.main",
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="email"
                    placeholder="Email Address"
                    type="email"
                    variant="outlined"
                    value={formData.email}
                    onChange={handleChange}
                    error={!!errors.email}
                    helperText={errors.email}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        backgroundColor: "background.paper",
                        "&:hover fieldset": {
                          borderColor: "primary.main",
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    id="phone"
                    name="phone"
                    label="Phone Number"
                    value={formData.phone.replace("+91", "")}
                    onChange={handleChange}
                    error={!!errors.phone}
                    helperText={errors.phone}
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon color={errors.phone ? 'error' : 'action'} 
                          sx={{ mr: 0.8 }}   // adds small right margin
                          />
                          <Typography
                            sx={{ fontWeight: 600, color: "text.secondary" }}
                          >
                            +91
                          </Typography>
                        </InputAdornment>
                      ),
                      inputProps: {
                        inputMode: "numeric",
                        pattern: "[0-9]{10}",
                      },
                    }}
                    sx={{
                      mb: 2,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        "& fieldset": {
                          borderColor: errors.phone
                            ? "error.main"
                            : "divider",
                        },
                        "&:hover fieldset": {
                          borderColor: errors.phone
                            ? "error.main"
                            : "primary.main",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: errors.phone
                            ? "error.main"
                            : "primary.main",
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="message"
                    placeholder="I am interested in learning more about your insurance plans. Please contact me."
                    variant="outlined"
                    multiline
                    rows={3}
                    value={formData.message}
                    onChange={handleChange}
                    error={!!errors.message}
                    helperText={errors.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment
                          position="start"
                          sx={{ alignSelf: "flex-start", mt: 1 }}
                        >
                          <MessageIcon color={errors.message ? 'error' : 'action'} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        backgroundColor: "background.paper",
                        "&:hover fieldset": {
                          borderColor: errors.message ? "error.main" : "primary.main",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: errors.message ? "error.main" : "primary.main",
                        },
                        "& textarea": {
                          ml: 1,
                          color: formData.message === "I am interested in learning more about your insurance plans. Please contact me." 
                            ? theme.palette.text.secondary 
                            : theme.palette.text.primary,
                          fontStyle: formData.message === "I am interested in learning more about your insurance plans. Please contact me." 
                            ? 'italic' 
                            : 'normal',
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={isSubmitting}
                    fullWidth
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    sx={{
                      mt: 1,
                      py: 1.5,
                      borderRadius: 2,
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      textTransform: "none",
                      letterSpacing: "0.5px",
                      background: (theme) => theme.palette.mode === 'dark'
                        ? "linear-gradient(45deg, #90caf9 30%, #64b5f6 90%)"
                        : "linear-gradient(45deg, #1976d2 30%, #0d47a1 90%)",
                      boxShadow: "0 4px 15px rgba(25, 118, 210, 0.3)",
                      transform: isHovered ? "translateY(-2px)" : "none",
                      transition:
                        "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow:
                          "0 6px 20px rgba(25, 118, 210, 0.4)",
                        background: (theme) => theme.palette.mode === 'dark'
                          ? "linear-gradient(45deg, #bbdefb 30%, #90caf9 90%)"
                          : "linear-gradient(45deg, #1e88e5 30%, #1565c0 90%)",
                      },
                    }}
                  >
                    {isSubmitting ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Get Free Consultation"
                    )}
                  </Button>

                  <Typography
                    variant="caption"
                    display="block"
                    align="center"
                    sx={{
                      mt: 2,
                      color: "text.secondary",
                      fontSize: "0.8rem",
                    }}
                  >
                    We respect your privacy. Your information is secure with us.
                  </Typography>
                </Grid>
              </Grid>
            </form>
          </Grid>
        </Grid>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ 
          mt: 8,
          '& .MuiPaper-root': {
            width: '100%',
            maxWidth: '600px',
            mx: 'auto',
            boxShadow: theme.shadows[6]
          }
        }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ 
            width: '100%',
            '& .MuiAlert-message': {
              fontWeight: 500,
              fontSize: '1rem'
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Contact;
